// The check-off list on the My Deals collection cards and the best deal's Add
// button above them (see modules/collections/templates/my-deals-card.php and
// my-deals-best.php).
//
// MARKUP CONTRACT
//   <section class="son-my-deals-best" data-son-my-deals-best>
//     <p data-son-my-deals-error hidden></p>
//     <button data-son-my-deals-best-add="{collection id}">
//   <section class="son-my-deals-card" data-son-my-deals-card="{collection id}">
//     <p data-son-my-deals-error hidden></p>
//     <details class="son-my-deals-card__details">
//       <input class="son-my-deals-item__check" data-son-my-deals-check="{product id}">
//
// A tick is a real add to cart, not a preview: completion is a property of the
// cart, so every price on the card moves when one product joins it. The route
// changes the cart and returns the card rendered again - no price is ever
// computed here. Every cart change can also move the best deal to another
// collection, so both routes return that box as well. The listeners are
// delegated from the document because the elements replace themselves.
//
// The cart also changes outside these cards, mostly from the header's mini cart.
// On the cart page the cards sit inside the cart form and come back with it; on
// the account screen the whole screen is fetched again, since a card can appear
// or drop out as well.

import { start as startRecalculating, cancel as cancelRecalculating } from './discount-toast';
import { confirmRemoving } from './cart-empty-confirm';
import { pushCartEvent } from './gtm4wp'; // these controls are no Woo buttons; the routes send their items

const config = () => window.sonMyDeals;
const controls = '[data-son-my-deals-check], [data-son-my-deals-best-add]';

// One cart change at a time across the whole screen, not per card: WooCommerce writes the
// whole session back at the end of a request, so two requests in flight drop each other's
// change. Real disabled because aria-busy's pointer-events do not stop keyboard input.
// On the cart page that includes the cart table's own controls.
const cartTable = '.woocommerce-cart-form__contents';
let busy = false;

function lock(locked) {
    busy = locked;
    document.querySelectorAll(controls).forEach((control) => {
        control.disabled = locked;
    });
    // readOnly, not disabled: Woo manages the update button's disabled state itself
    document.querySelectorAll(`${cartTable} input.qty`).forEach((input) => {
        input.readOnly = locked;
    });
}

// capturing, ahead of Woo's handlers delegated from body; anchors cannot be disabled
document.addEventListener(
    'click',
    (event) => {
        if (busy && event.target.closest(`${cartTable} a, ${cartTable} button, ${cartTable} input[type=submit]`)) {
            event.preventDefault();
            event.stopImmediatePropagation();
        }
    },
    true
);
document.addEventListener(
    'keydown',
    (event) => {
        if (busy && event.key === 'Enter' && event.target.closest(cartTable)) {
            event.preventDefault();
            event.stopImmediatePropagation();
        }
    },
    true
);

function showError(card, message) {
    const box = card.querySelector('[data-son-my-deals-error]');
    if (box) {
        box.textContent = message;
        box.hidden = !message;
    }
}

function applyFragments(fragments) {
    Object.entries(fragments || {}).forEach(([selector, html]) => {
        jQuery(selector).replaceWith(html);
    });
}

async function request(endpoint, body = null) {
    const response = await fetch(endpoint, {
        method: body ? 'POST' : 'GET',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'X-WP-Nonce': config().nonce },
        body: body ? JSON.stringify(body) : null,
    }).catch(() => {
        throw new Error(config().error);
    });
    // an error page from a PHP fatal is not JSON, and its parse error is not for the customer
    const json = await response.json().catch(() => null);
    if (!response.ok || !json) {
        throw new Error(json?.message || config().error);
    }

    return json;
}

function fromHtml(html) {
    const template = document.createElement('template');
    template.innerHTML = html.trim();

    return template.content.firstElementChild;
}

// the cart page has no best deal box, and its toggles leave it at that
function replaceBest(html) {
    const best = document.querySelector('[data-son-my-deals-best]');
    if (best && html) {
        best.replaceWith(fromHtml(html));
    }
}

// the open list is this browser's state, not the server's, and has to be carried
// over to the new card by hand
function keepOpen(card, next) {
    const details = card.querySelector('.son-my-deals-card__details');
    const nextDetails = next?.querySelector('.son-my-deals-card__details');

    if (details && nextDetails) {
        nextDetails.open = details.open;
    }
}

function replaceCard(card, html) {
    const next = fromHtml(html);

    keepOpen(card, next);
    card.replaceWith(next);

    return next;
}

function replaceScreen(screen, html) {
    const next = fromHtml(html);

    screen.querySelectorAll('[data-son-my-deals-card]').forEach((card) => {
        keepOpen(card, next.querySelector(`[data-son-my-deals-card="${card.dataset.sonMyDealsCard}"]`));
    });
    screen.replaceWith(next);
}

async function toggle(input) {
    const card = input.closest('[data-son-my-deals-card]');
    const productId = input.dataset.sonMyDealsCheck;
    const selected = input.checked;
    // read before lock(): disabling the focused checkbox moves focus to the body
    const focused = input === document.activeElement;

    card.setAttribute('aria-busy', 'true');
    lock(true);
    showError(card, '');
    startRecalculating();

    try {
        const result = await request(config().endpoint, {
            collection_id: Number(card.dataset.sonMyDealsCard),
            product_id: Number(productId),
            selected,
        });
        const check = replaceCard(card, result.html).querySelector(`[data-son-my-deals-check="${productId}"]`);
        replaceBest(result.best);
        if (focused) {
            check?.focus();
        }

        applyFragments(result.fragments);
        pushCartEvent('add_to_cart', result.added_items);
        pushCartEvent('remove_from_cart', result.removed_items);
        // the checkbox goes along as the "button" so cart-burst can anchor to it, and so
        // the account screen knows not to fetch itself again
        jQuery(document.body).trigger(selected ? 'added_to_cart' : 'removed_from_cart', [
            result.fragments,
            result.cart_hash,
            jQuery(check),
        ]);
    } catch (error) {
        input.checked = !selected;
        card.removeAttribute('aria-busy');
        cancelRecalculating();
        showError(card, error.message);
    } finally {
        lock(false);
    }

    if (focused && input.isConnected) {
        input.focus();
    }
}

async function complete(button) {
    const best = button.closest('[data-son-my-deals-best]');
    const collectionId = button.dataset.sonMyDealsBestAdd;

    best.setAttribute('aria-busy', 'true');
    lock(true);
    showError(best, '');
    startRecalculating();

    try {
        const result = await request(config().completeEndpoint, { collection_id: Number(collectionId) });

        applyFragments(result.fragments);
        pushCartEvent('add_to_cart', result.added_items);
        // before the swap: cart-burst measures the button, and the box that replaces it may
        // be another collection's
        jQuery(document.body).trigger('added_to_cart', [result.fragments, result.cart_hash, jQuery(button)]);

        const card = document.querySelector(`[data-son-my-deals-card="${collectionId}"]`);
        const next = card ? replaceCard(card, result.html) : null;
        replaceBest(result.best);
        if (next && result.error) {
            showError(next, result.error);
        }
    } catch (error) {
        best.removeAttribute('aria-busy');
        cancelRecalculating();
        showError(best, error.message);
    } finally {
        lock(false);
    }
}

async function reloadScreen() {
    const screen = document.querySelector('[data-son-my-deals-screen]');
    if (!screen) {
        return;
    }

    screen.setAttribute('aria-busy', 'true');
    lock(true);

    try {
        replaceScreen(screen, (await request(config().screenEndpoint)).html);
    } catch (error) {
        window.location.reload();
    } finally {
        lock(false);
    }
}

// queued, so a second mini cart removal does not race the first one's answer
let screenReload = Promise.resolve();

// on document (not body): the bundle loads in <head> before <body> exists
jQuery(document).on('added_to_cart removed_from_cart', (event, fragments, cartHash, $button) => {
    if (!config()) {
        return;
    }

    if (document.querySelector('.woocommerce-cart-form')) {
        // the cart page reloads its form on added_to_cart by itself, but not on removed_from_cart
        if (event.type === 'removed_from_cart') {
            jQuery(document.body).trigger('wc_update_cart');
        }
        return;
    }

    if (!jQuery($button).closest(controls).length) {
        screenReload = screenReload.then(reloadScreen);
    }
});

document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-son-my-deals-best-add]');
    if (button && config()) {
        complete(button);
    }
});

// A card's product checkbox: checked adds the product to the cart, unchecked removes it.
// Unchecking the cart's last product asks first; "keep" ticks the box again.
document.addEventListener('change', (event) => {
    const input = event.target.closest('[data-son-my-deals-check]');
    if (!input || !config()) {
        return;
    }

    if (input.checked) {
        toggle(input);
        return;
    }

    confirmRemoving(input.dataset.sonMyDealsCheck).then((confirmed) => {
        if (confirmed) {
            toggle(input);
        } else {
            input.checked = true;
        }
    });
});
